import { Subscribable, Subscription } from '@benzinga/subscribable';
import { UserSettingConfig } from './entities';
import { UserSettingsManager, UserSettingsManagerEvent } from './manager';
import { Session } from '@benzinga/session';
import { SafePromise } from '@benzinga/safe-await';
import { deepEqual } from '@benzinga/utils';

export interface SettingsChangeEvent<V> {
  type: 'settings_feed:update';
  value: V;
}

export interface SettingChangeEvent<V> {
  type: 'setting_feed:update';
  value: V;
  key: string;
}

export type UserSettingsFeedEvent<V> = SettingsChangeEvent<V>;

export class UserSettingsFeed<
  KEY extends string = string,
  VER extends number = number,
  VAL extends object = object,
  ALL_VAL extends object = object,
> extends Subscribable<UserSettingsFeedEvent<VAL>> {
  private config: UserSettingConfig<KEY, VER, VAL, ALL_VAL>;
  private manager: UserSettingsManager;
  private managerSubscription: Subscription<UserSettingsManager>;
  private allSettings?: Record<string, object>;
  private rawValue: ALL_VAL | undefined;
  private value: VAL;
  constructor(config: UserSettingConfig<KEY, VER, VAL, ALL_VAL>, session: Session, manager: UserSettingsManager) {
    super();
    this.config = config;
    this.manager = manager;

    this.managerSubscription = manager.subscribe(this.managerEventHandler);
    session.onPreSessionClose(() => this.managerSubscription.unsubscribe());
    this.allSettings = this.manager.getUserSettings();
    this.rawValue = this.extractValue(this.allSettings);
    this.value = this.loadValue(this.rawValue, this.allSettings);
  }

  public getValue = (): VAL => {
    return this.value;
  };

  public getRawValue = (): ALL_VAL | undefined => {
    return this.rawValue;
  };

  public setValue = (value: VAL | ((prev: VAL) => VAL)) => {
    if (typeof value === 'function') {
      value = value(this.value);
    }
    return this.manager.setUserSetting(this.versionedKey(this.config.version), value);
  };

  public receiveValue = (value: VAL) => {
    this.manager.receiveUserSetting(this.versionedKey(this.config.version), value);
  };

  public fetchValue = async (force?: boolean): SafePromise<VAL> => {
    return await this.manager.fetchUserSettings(force).then(res => {
      if (res.ok) {
        return { ok: this.value };
      } else {
        return { err: res.err };
      }
    });
  };

  private versionedKey = (version: number): string => {
    return `${this.config.key}_v${version}`;
  };

  private loadValue = (rawValue?: ALL_VAL, allSettings?: Record<string, object>): VAL => {
    return this.config.loader(rawValue, allSettings);
  };

  private extractValue = (allSettings?: Record<string, object>): ALL_VAL | undefined => {
    const i = Array(this.config.version)
      .fill(0)
      .map((_, i) => i + 1)
      .reverse()
      .find(i => {
        const new_key = this.versionedKey(i);
        const val = allSettings?.[new_key];
        return val != null;
      });
    const new_key = i ? this.versionedKey(i) : undefined;
    const val = new_key ? allSettings?.[new_key] : undefined;
    return val as ALL_VAL | undefined;
  };

  private setSettings = (settings: Record<string, object> | undefined) => {
    this.allSettings = settings;
    this.rawValue = this.extractValue(settings);
    const newValue = this.loadValue(this.rawValue, settings);
    if (!deepEqual(newValue, this.value)) {
      this.value = newValue;
      this.dispatch({
        type: 'settings_feed:update',
        value: this.value,
      });
    }
  };

  private managerEventHandler = (event: UserSettingsManagerEvent): void => {
    switch (event.type) {
      case 'user_settings:stored': {
        this.setSettings(event.settings);
        break;
      }
    }
    return;
  };
}
